/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import type { IconType } from '@elastic/eui'

import type { NonEmptyArray } from '@modules/ts-utils'
import type { CSP } from '@modules/ui-types/providers'

export type ProviderInfo = {
  id: CSP
  iconType: IconType
  longTitle: string
  shortTitle: string
}

export type RegionInfo = {
  cspRegionId: string
}

const providers: NonEmptyArray<ProviderInfo> = [
  {
    id: `aws`,
    iconType: `logoAWS`,
    longTitle: `Amazon Web Services`,
    shortTitle: `AWS`,
  },
  {
    id: `azure`,
    iconType: `logoAzure`,
    longTitle: `Azure`,
    shortTitle: `Azure`,
  },
  {
    id: `gcp`,
    iconType: `logoGCP`,
    longTitle: `Google Cloud`,
    shortTitle: `GCP`,
  },
]

export function getProviderInfoById(providerId: CSP): ProviderInfo {
  return providers.find(({ id }) => id === providerId) || providers[0]
}

export function getProviderIdFromRegionId(regionId: string): CSP {
  if (regionId.startsWith('azure')) {
    return 'azure'
  }

  if (regionId.startsWith('gcp')) {
    return 'gcp'
  }

  // Some APIs provide a prefix for AWS, but some (older ones) don't -- so treat
  // it as the fallback and no need to explicitly check.
  return 'aws'
}

/**
 * Strips the CSP prefix from a region ID, if any
 */
export function getCspRegionIdFromRegionId(regionId: string): string {
  return regionId.replace(/^(?:aws-|azure-|gcp-)/, '')
}

/**
 * Convenience method to retrieve all available metadata about a region from the ID.
 */
export function getRegionInfoById(regionId: string): {
  provider: ProviderInfo
  region: RegionInfo
} {
  const providerId = getProviderIdFromRegionId(regionId)
  const cspRegionId = getCspRegionIdFromRegionId(regionId)

  const provider = getProviderInfoById(providerId)
  const region: RegionInfo = {
    cspRegionId,
  }

  return { provider, region }
}
